// Pilot-release gating primitives. During the Pilot release a handful of
// surfaces are visible but not yet switched on — these wrappers give us one
// consistent vocabulary for that state.
//
//   • PilotTooltip — short floating card portaled to <body>, "we'll email you
//     when it's ready".
//   • PilotLock — dims the wrapped element, stamps a corner lock chip, swallows
//     clicks, surfaces PilotTooltip on hover/click.
//   • PilotBarrier — blurs a larger surface behind a centered "Not ready yet
//     in Pilot" card. For larger regions (Smell radar, Make/Assess panels).
//   • PilotBadge — small inline "SOON" tag for section headers.
//   • usePilotInline — lock-as-handler for inline buttons that can't host
//     PilotLock without disturbing layout (the prompt-create button, etc.).

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/Icon/Icon";

const PILOT_MSG = "Coming after Pilot — we'll email you when it's ready.";

type Rect = { left: number; top: number; right: number; bottom: number; width: number; height: number };

function PilotTooltip({ rect }: { rect: Rect | null }) {
  if (!rect) return null;
  const gap = 9;
  const maxW = 232;
  let left = rect.left + rect.width / 2;
  left = Math.max(maxW / 2 + 8, Math.min(left, window.innerWidth - maxW / 2 - 8));
  const below = rect.top < 90;
  const top = below ? rect.bottom + gap : rect.top - gap;

  return createPortal(
    <div
      role="tooltip"
      style={{
        position: "fixed",
        left,
        top,
        transform: `translate(-50%, ${below ? "0" : "-100%"})`,
        maxWidth: maxW,
        background: "var(--ink)",
        color: "var(--bg)",
        padding: "7px 11px",
        borderRadius: 6,
        boxShadow: "var(--shadow-2)",
        zIndex: 2000,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        gap: 7,
        fontSize: 11.5,
        lineHeight: 1.4,
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, opacity: 0.8 }}>
        <Icon name="lock" size={11} />
      </span>
      <span>{PILOT_MSG}</span>
    </div>,
    document.body,
  );
}

type PilotLockProps = {
  children: ReactNode;
  /** Stretch to fill a grid/flex cell — defaults to inline-grid. */
  block?: boolean;
  /** Show the corner lock chip. */
  badge?: boolean;
  badgeInset?: number;
  style?: CSSProperties;
};

export function PilotLock({
  children,
  block = false,
  badge = true,
  badgeInset = 6,
  style,
}: PilotLockProps) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const measure = () => {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!(hover || pinned)) return;
    measure();
    const onScroll = () => measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [hover, pinned]);

  useEffect(() => {
    if (!pinned) return;
    const onDocDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPinned(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPinned(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned]);

  const open = hover || pinned;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPinned((p) => !p);
    measure();
  };

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
      style={{
        position: "relative",
        cursor: "not-allowed",
        ...(block ? { display: "grid" } : { display: "inline-grid" }),
        ...style,
      }}
    >
      <div
        style={{
          pointerEvents: "none",
          opacity: open ? 0.62 : 0.5,
          filter: "grayscale(0.55)",
          transition: "opacity 130ms ease",
          display: "grid",
        }}
      >
        {children}
      </div>

      {badge && (
        <span
          style={{
            position: "absolute",
            top: badgeInset,
            right: badgeInset,
            width: 18,
            height: 18,
            borderRadius: 5,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: open ? "var(--ink)" : "var(--bg-sunk)",
            color: open ? "var(--panel)" : "var(--ink-3)",
            border: "1px solid var(--line)",
            boxShadow: "var(--shadow-1)",
            transition: "background 130ms ease, color 130ms ease",
            pointerEvents: "none",
          }}
        >
          <Icon name="lock" size={10} />
        </span>
      )}

      {open && <PilotTooltip rect={rect} />}
    </div>
  );
}

type PilotBarrierProps = {
  children: ReactNode;
  /** Stretch to fill a flex parent. */
  fill?: boolean;
  compact?: boolean;
  /** When true, render children untouched. Useful when the same component
   *  needs to flip the lock off in non-pilot environments. */
  off?: boolean;
  /** Blur radius (px) applied to the dimmed children. Lower = more legible. */
  blur?: number;
  /** Opacity of the dimmed children (0–1). Higher = more visible. */
  dim?: number;
  /** Scrim veil strength (%) — how much of `scrimColor` is mixed over the surface. */
  scrim?: number;
  /** Scrim veil color (any CSS color). */
  scrimColor?: string;
  /** Fixed width for the "coming soon" card; also keeps the email line on one line. */
  cardWidth?: number;
};

export function PilotBarrier({
  children,
  fill = false,
  compact = false,
  off = false,
  blur = 4,
  dim = 0.72,
  scrim = 16,
  scrimColor = "var(--bg)",
  cardWidth,
}: PilotBarrierProps) {
  if (off) return <>{children}</>;
  return (
    <div
      style={
        fill
          ? { position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
          : { position: "relative" }
      }
    >
      <div
        aria-hidden
        style={{
          ...(fill ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : {}),
          filter: `blur(${blur}px) saturate(0.9)`,
          opacity: dim,
          pointerEvents: "none",
          userSelect: "none",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 18,
          background: `color-mix(in srgb, ${scrimColor} ${scrim}%, transparent)`,
        }}
      >
        <div
          className="vstack"
          style={{
            alignItems: "center",
            textAlign: "center",
            gap: compact ? 8 : 11,
            maxWidth: cardWidth ?? (compact ? 220 : 320),
            padding: compact ? "16px 18px" : "26px 28px",
            background: "var(--panel)",
            border: "1px solid var(--line-strong)",
            borderRadius: 12,
            boxShadow: "var(--shadow-lift)",
          }}
        >
          <span
            style={{
              width: compact ? 34 : 42,
              height: compact ? 34 : 42,
              borderRadius: 999,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--bg-sunk)",
              color: "var(--ink-2)",
              border: "1px solid var(--line)",
            }}
          >
            <Icon name="lock" size={compact ? 15 : 19} />
          </span>
          <div className="vstack" style={{ gap: 4, alignItems: "center" }}>
            <span
              className="mono"
              style={{
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--ink-4)",
              }}
            >
              Coming soon
            </span>
            <span
              style={{
                fontSize: compact ? 14 : 17,
                fontWeight: 600,
                color: "var(--ink)",
                letterSpacing: "-0.01em",
              }}
            >
              Not ready yet in Pilot
            </span>
          </div>
          <div
            className="hstack"
            style={{ gap: 7, alignItems: "center", color: "var(--ink-3)" }}
          >
            <span style={{ color: "var(--accent-ink)", display: "inline-flex", flexShrink: 0 }}>
              <Icon name="mail" size={compact ? 12 : 13} />
            </span>
            <span
              style={{
                fontSize: compact ? 11.5 : 12.5,
                lineHeight: 1.4,
                whiteSpace: cardWidth ? "nowrap" : undefined,
              }}
            >
              We'll email you the moment it's ready.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PilotBadge({ children = "Soon" }: { children?: ReactNode }) {
  return (
    <span
      className="mono"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        padding: "2px 6px 2px 5px",
        borderRadius: 999,
        background: "var(--bg-sunk)",
        color: "var(--ink-3)",
        border: "1px solid var(--line)",
      }}
    >
      <Icon name="lock" size={9} />
      {children}
    </span>
  );
}

// Lock-as-handler for inline buttons where a wrapping PilotLock would
// disturb layout (segmented controls, the prompt-create chip). Returns
// handler props to spread onto the trigger plus the tooltip JSX to render
// alongside it.
export function usePilotInline() {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const ref = useRef<HTMLElement | null>(null);

  const measure = () => {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!pinned) return;
    const onDocDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPinned(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPinned(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned]);

  const open = hover || pinned;

  const bind = {
    ref: ref as React.RefObject<HTMLButtonElement | null>,
    onMouseEnter: () => {
      setHover(true);
      measure();
    },
    onMouseLeave: () => setHover(false),
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setPinned((p) => !p);
      measure();
    },
  };

  const tip = open ? <PilotTooltip rect={rect} /> : null;
  return { bind, tip, open };
}
